import './scrollytelling.css'

import { ArrowDownIcon } from '@heroicons/react/24/outline'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'

import { CHAPTERS, HERO, OUTRO, SIDES } from './content'
import { SceneProvider, useScrollStory } from './engine'
import { Avatar, Check, Shape } from './primitives'
import SceneAssistant from './scenes/SceneAssistant'
import SceneCrossMatch from './scenes/SceneCrossMatch'
import SceneData from './scenes/SceneData'
import SceneFeedback from './scenes/SceneFeedback'
import SceneGames from './scenes/SceneGames'
import SceneHire from './scenes/SceneHire'
import SceneInvitation from './scenes/SceneInvitation'
import SceneMatch from './scenes/SceneMatch'
import SceneRequirements from './scenes/SceneRequirements'
import SceneSecurity from './scenes/SceneSecurity'

const SCENES = {
  stelle: SceneRequirements,
  einladung: SceneInvitation,
  spiele: SceneGames,
  feedback: SceneFeedback,
  passung: SceneMatch,
  assistent: SceneAssistant,
  kreuzmatching: SceneCrossMatch,
  sicherheit: SceneSecurity,
  daten: SceneData,
  zusage: SceneHire
}

const STAGE = { w: 960, h: 640 }
const pad = (i) => String(i + 1).padStart(2, '0')

export default function Scrollytelling() {
  const story = useScrollStory(CHAPTERS.length)

  return (
    <div className='sty-root'>
      <Hero />

      <section
        ref={story.storyRef}
        className='sty-story'
        aria-label='Geschichte einer Bewerbung'
      >
        <Stage {...story} />

        <div className='sty-text'>
          {CHAPTERS.map((chapter, i) => (
            <div
              key={chapter.id}
              ref={(el) => (story.chapterRefs.current[i] = el)}
              className='sty-chapter'
              id={`kapitel-${chapter.id}`}
            >
              {chapter.steps.map((step, s) => (
                <article
                  key={step.title}
                  ref={(el) => (story.stepRefs.current[`${i}-${s}`] = el)}
                  className='sty-step'
                >
                  <div className='sty-step__card'>
                    {s === 0 && (
                      <div
                        className='sty-step__eyebrow'
                        data-side={chapter.side}
                      >
                        <span className='sty-step__dot' />
                        Kapitel {pad(i)} · {chapter.label} ·{' '}
                        {SIDES[chapter.side].label}
                      </div>
                    )}
                    <h2 className='sty-step__title'>{step.title}</h2>
                    <p className='sty-step__text'>{step.text}</p>
                  </div>
                </article>
              ))}
            </div>
          ))}
        </div>
      </section>

      <Outro />
    </div>
  )
}

// ---------- Bühne ----------

function Stage({ active, sceneRefs, subscribe, progressRef, scrollToChapter }) {
  const boxRef = useRef(null)
  const canvasRef = useRef(null)
  const side = CHAPTERS[active].side

  useEffect(() => {
    const box = boxRef.current
    if (!box) return undefined
    const fit = () => {
      const s = Math.min(box.clientWidth / STAGE.w, box.clientHeight / STAGE.h)
      canvasRef.current.style.transform = `translate(-50%, -50%) scale(${s})`
    }
    fit()
    const ro = new ResizeObserver(fit)
    ro.observe(box)
    return () => ro.disconnect()
  }, [])

  return (
    <div className='sty-stage-wrap'>
      <div className='sty-stage'>
        <div ref={progressRef} className='sty-stage__head'>
          <div className='sty-lanes' data-side={side}>
            <span className='sty-lanes__pill' />
            <span
              className={clsx('sty-lanes__item', side !== 'talent' && 'is-on')}
            >
              <Avatar initials='M' color='blue' size={22} /> Kundenseite
            </span>
            <span className={clsx('sty-lanes__item', side !== 'hr' && 'is-on')}>
              <Avatar initials='L' color='pink' size={22} /> Talentseite
            </span>
          </div>
          <div className='sty-stage__count'>
            <span className='sty-strong'>{CHAPTERS[active].label}</span>
            <span className='sty-muted'>
              {pad(active)} / {pad(CHAPTERS.length - 1)}
            </span>
          </div>
          <nav className='sty-progress-nav' aria-label='Kapitel'>
            <div className='sty-progress-nav__fill' />
            {CHAPTERS.map((c, i) => (
              <button
                key={c.id}
                type='button'
                className={clsx(
                  'sty-progress-nav__dot',
                  i <= active && 'is-done'
                )}
                style={{ left: `${(i / (CHAPTERS.length - 1)) * 100}%` }}
                onClick={() => scrollToChapter(i)}
                aria-label={`Kapitel ${pad(i)}: ${c.label}`}
                title={c.label}
              />
            ))}
          </nav>
        </div>

        <div ref={boxRef} className='sty-stage__box' aria-hidden='true'>
          <div
            ref={canvasRef}
            className='sty-canvas'
            style={{ width: STAGE.w, height: STAGE.h }}
          >
            {CHAPTERS.map((c, i) => {
              const Scene = SCENES[c.id]
              return (
                <div
                  key={c.id}
                  ref={(el) => (sceneRefs.current[i] = el)}
                  className='sty-scene'
                >
                  <SceneProvider index={i} subscribe={subscribe}>
                    <Scene />
                  </SceneProvider>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

Stage.propTypes = {
  active: PropTypes.number.isRequired,
  sceneRefs: PropTypes.object.isRequired,
  subscribe: PropTypes.func.isRequired,
  progressRef: PropTypes.object.isRequired,
  scrollToChapter: PropTypes.func.isRequired
}

// ---------- Hero und Outro ----------

const HERO_SHAPES = [
  { kind: 'tri', color: 'yellow', size: 56, x: '8%', y: '14%', r: -14, d: 0 },
  { kind: 'sq', color: 'green', size: 40, x: '78%', y: '8%', r: 18, d: 1.2 },
  { kind: 'dot', color: 'pink', size: 28, x: '86%', y: '70%', r: 0, d: 0.6 },
  { kind: 'tri', color: 'blue', size: 36, x: '16%', y: '78%', r: 28, d: 1.8 },
  { kind: 'sq', color: 'pink', size: 22, x: '50%', y: '4%', r: 40, d: 2.4 },
  { kind: 'dot', color: 'yellow', size: 18, x: '44%', y: '88%', r: 0, d: 0.9 }
]

function Hero() {
  return (
    <header className='sty-hero'>
      <div className='sty-hero__text'>
        <div className='sty-hero__eyebrow'>{HERO.eyebrow}</div>
        <h1 className='sty-hero__title'>{HERO.title}</h1>
        <p className='sty-hero__lead'>{HERO.text}</p>
        <div className='sty-hero__cue'>
          <ArrowDownIcon width={18} height={18} />
          {HERO.cue}
        </div>
      </div>

      <div className='sty-hero__art' aria-hidden='true'>
        {HERO_SHAPES.map((s, i) => (
          <span
            key={i}
            className='sty-float'
            style={{ left: s.x, top: s.y, animationDelay: `${s.d}s` }}
          >
            <Shape kind={s.kind} color={s.color} size={s.size} rotate={s.r} />
          </span>
        ))}
        <svg
          className='sty-hero__path'
          viewBox='0 0 400 300'
          preserveAspectRatio='none'
        >
          <path d='M70 210 C 150 40, 250 260, 330 90' />
        </svg>
        <div className='sty-hero__person' style={{ left: '8%', top: '56%' }}>
          <Avatar initials='M' color='blue' size={88} />
          <span>Mara sucht</span>
        </div>
        <div className='sty-hero__person' style={{ left: '70%', top: '14%' }}>
          <Avatar initials='L' color='pink' size={88} />
          <span>Lena bewirbt sich</span>
        </div>
      </div>
    </header>
  )
}

function Outro() {
  const toTop = () => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' })
  }

  return (
    <footer className='sty-outro'>
      <div className='sty-outro__card'>
        <div className='sty-outro__shapes' aria-hidden='true'>
          <Shape kind='tri' color='yellow' size={28} rotate={-10} />
          <Shape kind='sq' color='green' size={22} rotate={14} />
          <Shape kind='dot' color='pink' size={22} />
        </div>
        <h2 className='sty-outro__title'>{OUTRO.title}</h2>
        <ul className='sty-outro__list'>
          {OUTRO.points.map((p) => (
            <li key={p}>
              <Check size={22} color='blue' />
              {p}
            </li>
          ))}
        </ul>
        <div className='sty-outro__actions'>
          <Link to={OUTRO.primary.to} className='sty-cta'>
            {OUTRO.primary.label}
          </Link>
          <button
            type='button'
            onClick={toTop}
            className='sty-cta sty-cta--ghost'
          >
            {OUTRO.secondary}
          </button>
        </div>
      </div>
    </footer>
  )
}
